import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { AIPreset, AIPresetCategory, AIProductStagingRule, AIVirtualModelRule, AIToolUsageRule } from '@zakeke/zakeke-configurator-react';
import { useZakeke, AIRateLimitError } from '@zakeke/zakeke-configurator-react';
import { useFireUserFileUpload } from './hooks';
import { useDialogManager } from '../dialogs/Dialogs';
import ErrorDialog from '../dialogs/ErrorDialog';

export type PresetTypeProductStaging = 'mood' | 'background' | 'effect' | 'surface';
export type PresetTypeVirtualTryOn = 'person' | 'pose' | 'background' | 'effect';
export type PresetType = PresetTypeProductStaging | PresetTypeVirtualTryOn;

export type UploadedImage = {
	imageUrl: string;
	sourceImageUrl: string | null;
};

export type ImageUrlState = {
	selectedUrl: string;
	uploadedUrls: UploadedImage[];
	isUploading?: boolean;
};

export type ImageUrlByPresetType = {
	[K in PresetType]?: ImageUrlState;
};

export type PanelDataState = {
	presets: AIPreset[];
	categories?: AIPresetCategory[];
	isLoading: boolean;
};

const urlToBase64 = async (url: string): Promise<string> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'Anonymous';
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.drawImage(img, 0, 0);
				const dataURL = canvas.toDataURL('image/png');
				resolve(dataURL);
			} else {
				reject(new Error('Failed to get canvas context'));
			}
		};
		img.onerror = () => {
			reject(new Error('Failed to load image'));
		};
		img.src = url;
	});
};

const useFetchAIPresets = () => {
	const { getAIPresetCategories, getAIPresetPresets } = useZakeke();

	// Use refs to stabilize function references and prevent loadPanelData effect re-triggers
	const getAIPresetPresetsRef = useRef(getAIPresetPresets);
	getAIPresetPresetsRef.current = getAIPresetPresets;
	const getAIPresetCategoriesRef = useRef(getAIPresetCategories);
	getAIPresetCategoriesRef.current = getAIPresetCategories;

	const fetchPresets = useCallback(
		async (presetType: PresetType): Promise<AIPreset[]> => {
			return await getAIPresetPresetsRef.current(presetType);
		},
		[]
	);

	const fetchCategories = useCallback(
		async (categoryType: PresetType): Promise<AIPresetCategory[]> => {
			return await getAIPresetCategoriesRef.current(categoryType);
		},
		[]
	);

	return {
		fetchPresets,
		fetchCategories,
	};
};

export const useAIProductStagingData = (initialMode?: 'product-staging' | 'virtual-tryon') => {
	const zakeke = useZakeke();
	const {
		product,
		generateAIProductStaging,
		generateAIVirtualTryon,
		getOnlineScreenshot,
		createImage,
		getApplicableAIStagingRules,
		getApplicableAIVirtualModelRules,
		T_static,
	} = zakeke;

	const T = useMemo(
		() => ({
			_: T_static,
			_d: T_static,
			_withOptions: T_static,
			_dWithOptions: T_static,
		}),
		[T_static]
	);

	const [currentAssetId] = useState(1);

	const fireUserFileUpload = useFireUserFileUpload();

	const [modalType, setModalType] = useState<'product-staging' | 'virtual-tryon'>(initialMode ?? 'product-staging');

	const { fetchPresets, fetchCategories } = useFetchAIPresets();
	const { showDialog, closeDialog } = useDialogManager();

	const [panelData, setPanelData] = useState<{
		mood: PanelDataState;
		person: PanelDataState;
		pose: PanelDataState;
		background: PanelDataState;
		surface: PanelDataState;
		effect: PanelDataState;
	}>({
		mood: { presets: [], categories: [], isLoading: false },
		person: { presets: [], categories: [], isLoading: false },
		pose: { presets: [], categories: [], isLoading: false },
		background: { presets: [], categories: [], isLoading: false },
		surface: { presets: [], categories: [], isLoading: false },
		effect: { presets: [], categories: [], isLoading: false },
	});

	const [modifiedImages, setModifiedImages] = useState<Array<string>>([]);
	const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);
	const [hasGenerated, setHasGenerated] = useState(false);
	const [activePanel, setActivePanel] = useState<string | null>(null);
	const [prompt, setPrompt] = useState('');

	const [selectedBackground, setSelectedBackground] = useState<number | null>(null);
	const [selectedPerson, setSelectedPerson] = useState<number | null>(null);
	const [selectedPose, setSelectedPose] = useState<number | null>(null);
	const [selectedSurface, setSelectedSurface] = useState<number | null>(null);
	const [selectedMood, setSelectedMood] = useState<number | null>(null);
	const [selectedEffect, setSelectedEffect] = useState<number | null>(null);

	const [imageUrl, setImageUrl] = useState<ImageUrlByPresetType>({});
	const [inputImage, setInputImage] = useState<string | null>(null);

	const [aiStagingRules, setAiStagingRules] = useState<AIProductStagingRule | null>(null);
	const [aiVtoRules, setAiVtoRules] = useState<AIVirtualModelRule | null>(null);
	const [allowCustomBackground, setAllowCustomBackground] = useState(true);
	const [rulesLoaded, setRulesLoaded] = useState(false);

	// Use refs for API functions to prevent effect re-triggers on function reference changes
	const getApplicableAIStagingRulesRef = useRef(getApplicableAIStagingRules);
	getApplicableAIStagingRulesRef.current = getApplicableAIStagingRules;
	const getApplicableAIVirtualModelRulesRef = useRef(getApplicableAIVirtualModelRules);
	getApplicableAIVirtualModelRulesRef.current = getApplicableAIVirtualModelRules;

	const loadPanelData = useCallback(
		async (panelType: PresetType) => {
			setPanelData((prev) => ({
				...prev,
				[panelType]: { ...prev[panelType], isLoading: true },
			}));

			try {
				const [loadedPresets, loadedCategories] = await Promise.all([
					fetchPresets(panelType),
					fetchCategories(panelType),
				]);

				setPanelData((prev) => ({
					...prev,
					[panelType]: {
						presets: loadedPresets,
						categories: loadedCategories,
						isLoading: false,
					},
				}));
			} catch (err) {
				console.error(`Error loading ${panelType} data:`, err);
				setPanelData((prev) => ({
					...prev,
					[panelType]: { ...prev[panelType], isLoading: false },
				}));
			}
		},
		[fetchPresets, fetchCategories]
	);

	useEffect(() => {
		const panelTypes: Array<PresetType> = ['mood', 'person', 'pose', 'background', 'surface', 'effect'];
		panelTypes.forEach((panelType) => {
			loadPanelData(panelType);
		});
	}, [loadPanelData]);

	useEffect(() => {
		let cancelled = false;
		setRulesLoaded(false);
		if (modalType === 'product-staging' && product?.id) {
			getApplicableAIStagingRulesRef.current({
				productID: product.id,
				printTypeID: undefined,
			})
				.then((rules) => {
					if (cancelled) return;
					setAiStagingRules(rules);
					setAllowCustomBackground(rules?.allowCustomBackground ?? true);
					setRulesLoaded(true);
				})
				.catch((error) => {
					if (cancelled) return;
					console.error('Error fetching AI Staging Rules:', error);
					setAiStagingRules(null);
					setAllowCustomBackground(true);
					setRulesLoaded(true);
				});
		} else if (modalType === 'virtual-tryon' && product?.id) {
			getApplicableAIVirtualModelRulesRef.current({
				productID: product.id,
				printTypeID: undefined,
			})
				.then((rules: AIVirtualModelRule | null) => {
					if (cancelled) return;
					setAiVtoRules(rules);
					setAiStagingRules(null);
					setAllowCustomBackground(rules?.allowCustomBackground ?? true);
					setRulesLoaded(true);
				})
				.catch((error: unknown) => {
					if (cancelled) return;
					console.error('Error fetching AI VTO Rules:', error);
					setAiVtoRules(null);
					setAiStagingRules(null);
					setAllowCustomBackground(true);
					setRulesLoaded(true);
				});
		} else if (modalType === 'virtual-tryon') {
			setAiVtoRules(null);
			setAiStagingRules(null);
			setAllowCustomBackground(true);
			setRulesLoaded(true);
		}
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalType, product?.id]);

	const handleSetActivePanel = useCallback((panelId: string | null) => {
		setActivePanel(panelId);
	}, []);

	const handleSelect = useCallback((type: string, value: number | null) => {
		switch (type) {
			case 'background':
				setSelectedBackground(value);
				if (value !== null) {
					setImageUrl((prev) => ({
						...prev,
						background: { selectedUrl: '', uploadedUrls: prev.background?.uploadedUrls || [] },
					}));
				}
				break;
			case 'person':
				setSelectedPerson(value);
				if (value !== null) {
					setImageUrl((prev) => ({
						...prev,
						person: { selectedUrl: '', uploadedUrls: prev.person?.uploadedUrls || [] },
					}));
				}
				break;
			case 'pose':
				setSelectedPose(value);
				break;
			case 'surface':
				setSelectedSurface(value);
				break;
			case 'mood':
				setSelectedMood(value);
				break;
			case 'effect':
				setSelectedEffect(value);
				break;
		}
	}, []);

	const handleGenerate = useCallback(async () => {
		if (isLoadingGenerate) return;

		setIsLoadingGenerate(true);

		let selectedImage = '';

		if (inputImage) {
			selectedImage = inputImage;
		} else {
			const screenshotResult = await getOnlineScreenshot(512, 512, false);
			if (screenshotResult?.originalUrl) {
				selectedImage = screenshotResult.originalUrl;
			}
		}

		if (!selectedImage) {
			setIsLoadingGenerate(false);
			return;
		}

		try {
			let productImageBase64 = selectedImage;
			if (selectedImage && !selectedImage.startsWith('data:')) {
				productImageBase64 = await urlToBase64(selectedImage);
			}

			let response: { imageUrl: string };

			if (modalType === 'virtual-tryon') {
				const generationData = {
					clothingImageUrl: productImageBase64 ?? '',
					backgroundType: selectedBackground ? 'preset' : 'none',
					backgroundPreset: selectedBackground || undefined,
					backgroundImageUrl: imageUrl?.background?.selectedUrl || '',
					effectPreset: selectedEffect || undefined,
					posePreset: selectedPose || undefined,
					personPreset: selectedPerson || undefined,
					personImageUrl: imageUrl?.person?.selectedUrl || undefined,
					customPrompt: prompt.trim() || undefined,
				};
				response = await generateAIVirtualTryon(generationData);
			} else {
				const generationData = {
					productImageUrl: productImageBase64 ?? '',
					backgroundType: selectedBackground ? 'preset' : 'none',
					backgroundPreset: selectedBackground || undefined,
					backgroundImageUrl: imageUrl?.background?.selectedUrl || '',
					surfacePreset: selectedSurface || undefined,
					moodPreset: selectedMood || undefined,
					effectPreset: selectedEffect || undefined,
					customPrompt: prompt.trim() || undefined,
				};
				response = await generateAIProductStaging(generationData);
			}

			setModifiedImages((prev) => {
				const newImages = [...prev, response.imageUrl];
				return newImages.slice(-4);
			});

			setHasGenerated(true);
			setActivePanel(null);
			setInputImage(null);
		} catch (error) {
			console.error('Generation error:', error);

			// Check if error is a rate limit error with toolUsageRule property
			const errorWithRule = error as Error | (Error & { toolUsageRule: AIToolUsageRule; lastGenerationTime?: number });
			if ('toolUsageRule' in errorWithRule) {
				const toolUsageRule = errorWithRule.toolUsageRule;
				const limitPeriodMinutes =
					errorWithRule.lastGenerationTime && toolUsageRule.limitPeriodMinutes
						? Math.floor(
								toolUsageRule.limitPeriodMinutes -
									(Date.now() - errorWithRule.lastGenerationTime) / 60000
							)
						: toolUsageRule.limitPeriodMinutes || 0;

				const isSessionLimit = toolUsageRule.limitPeriodType === 'Session';
				const title = T._('Generation Limit Reached');
				const message = isSessionLimit
					? T._("You've reached the maximum number of generations allowed per session.")
					: T._(
							"You've reached the maximum number of generations allowed. Please try again in {0} minutes."
						).replace('{0}', String(limitPeriodMinutes));

				showDialog(
					'limit',
					<ErrorDialog
						error={`${title}\n\n${message}`}
						onCloseClick={() => closeDialog('limit')}
					/>
				);
			} else {
				showDialog(
					'error',
					<ErrorDialog
						error={(error as Error)?.message || T._('Generic Error')}
						onCloseClick={() => closeDialog('error')}
					/>
				);
			}
		} finally {
			setIsLoadingGenerate(false);
		}
	}, [
		selectedBackground,
		selectedSurface,
		selectedMood,
		selectedEffect,
		prompt,
		isLoadingGenerate,
		getOnlineScreenshot,
		selectedPose,
		selectedPerson,
		generateAIProductStaging,
		generateAIVirtualTryon,
		modalType,
		showDialog,
		closeDialog,
		imageUrl?.background?.selectedUrl,
		imageUrl?.person?.selectedUrl,
		inputImage,
		T,
	]);

	const hasUserUpload = useCallback((presetType: PresetType): boolean => {
		return presetType === 'person' || presetType === 'background';
	}, []);

	const handleUpload = useCallback(
		(presetType: PresetType) => {
			fireUserFileUpload(async (file: File) => {
				setImageUrl((prev) => {
					const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
					return {
						...prev,
						[presetType]: {
							...currentState,
							isUploading: true,
						},
					};
				});

				try {
					const uploadedImageResult = await createImage(file);

					if (uploadedImageResult?.choiceUrl) {
						const uploadedImage: UploadedImage = {
							imageUrl: uploadedImageResult.choiceUrl,
							sourceImageUrl: null,
						};

						setImageUrl((prev) => {
							const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
							return {
								...prev,
								[presetType]: {
									selectedUrl: uploadedImageResult.choiceUrl,
									uploadedUrls: [...currentState.uploadedUrls, uploadedImage],
									isUploading: false,
								},
							};
						});

						if (presetType === 'background') {
							setSelectedBackground(null);
						} else if (presetType === 'person') {
							setSelectedPerson(null);
						}
					}
				} catch (error) {
					setImageUrl((prev) => ({
						...prev,
						[presetType]: { ...prev[presetType], isUploading: false },
					}));
					console.error(`Error uploading image for ${presetType}:`, error);
				}
			}, 'image/*');
		},
		[fireUserFileUpload, createImage]
	);

	/**
	 * Handle webcam capture for person preset
	 * Processes captured image from webcam
	 */
	const handleWebcamCapture = useCallback(
		async (file: File) => {
			const presetType: PresetType = 'person';

			// Set loading state
			setImageUrl((prev) => {
				const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
				return {
					...prev,
					[presetType]: {
						...currentState,
						isUploading: true,
					},
				};
			});

			try {
				const uploadedImageResult = await createImage(file);

				if (uploadedImageResult?.choiceUrl) {
					const uploadedImage: UploadedImage = {
						imageUrl: uploadedImageResult.choiceUrl,
						sourceImageUrl: null,
					};

					setImageUrl((prev) => {
						const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
						return {
							...prev,
							[presetType]: {
								selectedUrl: uploadedImageResult.choiceUrl,
								uploadedUrls: [...currentState.uploadedUrls, uploadedImage],
								isUploading: false,
							},
						};
					});

					// Clear selected person preset when webcam capture succeeds
					setSelectedPerson(null);
				}
			} catch (error) {
				setImageUrl((prev) => {
					const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
					return {
						...prev,
						[presetType]: { ...currentState, isUploading: false },
					};
				});
				console.error(`Error uploading webcam capture for ${presetType}:`, error);
			}
		},
		[createImage]
	);

	const handleDeleteVariant = useCallback((variantId: number) => {
		setModifiedImages((prev) => {
			const newImages = [...prev];
			newImages.splice(variantId, 1);
			if (newImages.length === 0) {
				setHasGenerated(false);
			}
			return newImages;
		});
	}, []);

	const filteredPanelData = useMemo(() => {
		// Don't expose any presets until rules are fully loaded to prevent tab flash
		if (!rulesLoaded) {
			return {
				mood: { ...panelData.mood, presets: [], categories: [] },
				person: { ...panelData.person, presets: [], categories: [] },
				pose: { ...panelData.pose, presets: [], categories: [] },
				background: { ...panelData.background, presets: [], categories: [] },
				surface: { ...panelData.surface, presets: [], categories: [] },
				effect: { ...panelData.effect, presets: [], categories: [] },
			};
		}

		// Apply Product Staging rules
		if (modalType === 'product-staging' && aiStagingRules) {
			const { backgroundCategoryIDs, surfacePresetIDs, moodPresetIDs, effectPresetIDs } = aiStagingRules;

			return {
				...panelData,
				background: {
					...panelData.background,
					categories: panelData.background.categories?.filter((cat) =>
						backgroundCategoryIDs.includes(Number(cat.id))
					),
					presets: panelData.background.presets.filter(
						(preset) =>
							preset.categoryId !== null && backgroundCategoryIDs.includes(Number(preset.categoryId))
					),
				},
				surface: {
					...panelData.surface,
					presets: panelData.surface.presets.filter((preset) => surfacePresetIDs.includes(Number(preset.id))),
				},
				mood: {
					...panelData.mood,
					presets: panelData.mood.presets.filter((preset) => moodPresetIDs.includes(Number(preset.id))),
				},
				effect: {
					...panelData.effect,
					presets: panelData.effect.presets.filter((preset) => effectPresetIDs.includes(Number(preset.id))),
				},
			};
		}

		// Apply Virtual Try-on rules
		if (modalType === 'virtual-tryon' && aiVtoRules) {
			const { backgroundCategoryIDs, effectPresetIDs, modelPresetIDs, poseCategoryIDs } = aiVtoRules;

			return {
				...panelData,
				person: {
					...panelData.person,
					presets: panelData.person.presets.filter((preset) => modelPresetIDs.includes(Number(preset.id))),
				},
				pose: {
					...panelData.pose,
					categories: panelData.pose.categories?.filter((cat) =>
						poseCategoryIDs.includes(Number(cat.id))
					),
					presets: panelData.pose.presets.filter(
						(preset) => preset.categoryId !== null && poseCategoryIDs.includes(Number(preset.categoryId))
					),
				},
				background: {
					...panelData.background,
					categories: panelData.background.categories?.filter((cat) =>
						backgroundCategoryIDs.includes(Number(cat.id))
					),
					presets: panelData.background.presets.filter(
						(preset) => preset.categoryId !== null && backgroundCategoryIDs.includes(Number(preset.categoryId))
					),
				},
				effect: {
					...panelData.effect,
					presets: panelData.effect.presets.filter((preset) => effectPresetIDs.includes(Number(preset.id))),
				},
			};
		}

		return panelData;
	}, [panelData, aiStagingRules, aiVtoRules, modalType, rulesLoaded]);

	useEffect(() => {
		if (!rulesLoaded) return;

		const pickRandom = (presets: AIPreset[]): number | null => {
			if (presets.length === 0) return null;
			return presets[Math.floor(Math.random() * presets.length)].id;
		};

		if (modalType === 'product-staging') {
			if (filteredPanelData.background.presets.length === 0) return;
			setSelectedBackground(pickRandom(filteredPanelData.background.presets));
			setSelectedSurface(pickRandom(filteredPanelData.surface.presets));
			setSelectedMood(pickRandom(filteredPanelData.mood.presets));
			setSelectedEffect(pickRandom(filteredPanelData.effect.presets));
		} else if (modalType === 'virtual-tryon') {
			if (filteredPanelData.person.presets.length === 0) return;
			setSelectedPerson(pickRandom(filteredPanelData.person.presets));
			setSelectedPose(pickRandom(filteredPanelData.pose.presets));
			setSelectedBackground(pickRandom(filteredPanelData.background.presets));
			setSelectedEffect(pickRandom(filteredPanelData.effect.presets));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rulesLoaded, modalType, filteredPanelData]);

	return {
		panelData: filteredPanelData,
		mood: filteredPanelData.mood,
		person: filteredPanelData.person,
		pose: filteredPanelData.pose,
		background: filteredPanelData.background,
		surface: filteredPanelData.surface,
		effect: filteredPanelData.effect,
		isLoadingPreset: Object.values(panelData).some((panel) => panel.isLoading) || !rulesLoaded,
		isLoadingGenerate,
		currentAssetId,
		hasGenerated,
		setHasGenerated,
		activePanel,
		setActivePanel: handleSetActivePanel,
		prompt,
		setPrompt,
		selectedBackground,
		setSelectedBackground,
		selectedPerson,
		setSelectedPerson,
		selectedPose,
		setSelectedPose,
		selectedSurface,
		setSelectedSurface,
		selectedMood,
		setSelectedMood,
		selectedEffect,
		setSelectedEffect,
		imageUrl,
		setImageUrl,
		inputImage,
		setInputImage,
		handleSelect,
		handleGenerate,
		handleUpload,
		handleDeleteVariant,
		handleWebcamCapture,
		hasUserUpload,
		modifiedImages,
		modalType,
		setModalType,
		allowCustomBackground,
	};
};
